import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiError } from '@/lib/pos-api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { SHORTCUT_KEYS, SHORTCUT_DEFAULTS, shortcutBinding, shortcutCatalog, shortcutTarget } from '@/lib/user-shortcuts.mjs';

export default function UserShortcuts({ groups, open, setOpen }) {
  const navigate = useNavigate();
  const catalog = useMemo(() => shortcutCatalog(groups), [groups]);
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    let active = true;
    setBusy(true); setError(''); setSaved(false);
    api.get('/auth/shortcuts').then(({ data }) => {
      if (active) { setSettings(data); setDraft(data); }
    }).catch(e => { if (active) setError(apiError(e)); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [open]);
  useEffect(() => {
    const keydown = event => {
      if (!shortcutBinding(event, settings, catalog)) return;
      const target = shortcutTarget(event, settings, catalog, open || !!document.querySelector('[role="dialog"], [role="alertdialog"], dialog[open]'));
      // An overridden key must never also execute its old cashier/browser action,
      // including when navigation is blocked inside a form or dialog.
      event.preventDefault(); event.stopImmediatePropagation();
      if (target) navigate(target);
    };
    window.addEventListener('keydown', keydown, true);
    return () => window.removeEventListener('keydown', keydown, true);
  }, [settings, catalog, open, navigate]);
  const update = (key, to) => {
    setSaved(false);
    setDraft(value => ({ ...value, bindings: [...value.bindings.filter(item => item.key !== key), ...(to ? [{ key, to }] : [])] }));
  };
  const save = async () => {
    setBusy(true); setError(''); setSaved(false);
    try {
      const { data } = await api.put('/auth/shortcuts', draft);
      setSettings(data); setDraft(data); setSaved(true);
    } catch (e) { setError(apiError(e)); }
    finally { setBusy(false); }
  };
  return <>
    <Dialog open={open} onOpenChange={value => { if (!busy) setOpen(value); }}>
      <DialogContent className="max-w-xl max-h-[85dvh] overflow-y-auto">
        <DialogHeader><DialogTitle>Pengaturan Shortcut Saya</DialogTitle><DialogDescription>Tersimpan untuk akun Anda. Pilih menu sesuai hak akses; tidak mengubah shortcut pengguna lain.</DialogDescription></DialogHeader>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        {draft && <fieldset disabled={busy} className="space-y-3">
          <label className="flex gap-2 items-center"><input type="checkbox" checked={draft.enabled} onChange={e => { setDraft({ ...draft, enabled: e.target.checked }); setSaved(false); }} />Aktifkan shortcut menu pribadi</label>
          {SHORTCUT_KEYS.map(key => {
            const current = draft.bindings.find(item => item.key === key)?.to || '';
            return <label key={key} className="flex gap-3 items-center"><kbd className="w-12 shrink-0 font-semibold">{key}</kbd>
              <select aria-label={`Menu ${key}`} className="min-w-0 flex-1 border rounded-md p-2 bg-background" value={current} onChange={e => update(key, e.target.value)}>
                <option value="">Default sistem — {SHORTCUT_DEFAULTS[key]}</option>
                {current && !catalog.some(item => item.to === current) && <option value={current}>Akses menu tidak tersedia — shortcut tidak aktif</option>}
                {catalog.map(item => <option key={item.to} value={item.to}>{item.label}</option>)}
              </select></label>;
          })}
          <Button onClick={save} disabled={busy}>{busy ? 'Memuat / menyimpan…' : 'Simpan Shortcut'}</Button>
          <Button type="button" variant="outline" className="ml-2" disabled={busy} onClick={() => { setDraft({ ...draft, enabled: false, bindings: [] }); setSaved(false); }}>Reset semua ke default</Button>
          <p className="text-xs text-ui-muted">Pilih Default sistem untuk mengembalikan satu tombol. Reset semua mengembalikan seluruh tombol. Tekan Simpan Shortcut untuk menerapkan.</p>
        </fieldset>}
        {saved && <p role="status" className="text-sm text-green-700">Shortcut tersimpan untuk akun Anda.</p>}
        <div className="text-sm space-y-2 border-t pt-3">
          <p className="font-semibold">Catatan tombol yang sudah digunakan</p>
          <p>F1: Produk (admin) · F2: Kasir / pencarian · F3: Pelanggan · F4: Keranjang · F8: Pembayaran.</p>
          <p>Enter: lanjut / simpan sesuai formulir · Esc: tutup popup · Control: aksi kasir sesuai posisi · Shift: varian · −: kurangi QTT sesuai posisi.</p>
          <p>F5: muat ulang · F11: layar penuh. Fungsi bawaan berlaku jika tombol memakai Default sistem atau shortcut pribadi dinonaktifkan.</p>
          <p>Shortcut pribadi hanya membuka menu, bukan posting transaksi. Saat kursor di kolom input atau popup terbuka, tombol yang diganti ditahan agar tidak menjalankan fungsi lama. Pilih area kosong dahulu untuk membuka menu.</p>
          <p>F6/F7/F10/F12 dapat digunakan browser atau perangkat. F12 mungkin memerlukan Fn; jika ditangkap perangkat, pilih tombol lain. Satu tombol hanya memiliki satu tujuan per akun.</p>
        </div>
      </DialogContent>
    </Dialog>
  </>;
}
